import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Copy,
  Download,
  Eraser,
  LayoutDashboard,
  Loader2,
  MoreHorizontal,
  RotateCcw,
  Smartphone,
  Upload,
  Check,
  AlertCircle,
  Info,
} from "lucide-react";

function useClickOutside(ref, onClose, isOpen) {
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [ref, onClose, isOpen]);
}

function MenuItem({ icon: Icon, label, onClick, disabled, danger, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger
          ? "text-red-600 hover:bg-red-50"
          : active
            ? "text-green-700 bg-green-50"
            : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      {Icon && <Icon size={15} className="flex-shrink-0 opacity-70" />}
      <span className="font-medium">{label}</span>
    </button>
  );
}

function MenuSection({ title, children }) {
  return (
    <div className="py-1">
      {title && (
        <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400">
          {title}
        </p>
      )}
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

export function FlowToolsMenu({
  onFormat,
  onFormatAll,
  showFormatAll,
  onClear,
  onCopyJSON,
  onImport,
  onExport,
  jsonCopied,
  nodesEmpty,
  flowNameEmpty,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const closeAnd = (fn) => () => {
    fn?.();
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-xl border transition-colors ${
          open
            ? "bg-gray-100 border-gray-400 text-gray-900"
            : "text-gray-700 border-gray-300 hover:bg-gray-50"
        }`}
      >
        <MoreHorizontal size={16} />
        Tools
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden">
          <MenuSection title="Layout">
            <MenuItem
              icon={LayoutDashboard}
              label="Format flow"
              onClick={closeAnd(onFormat)}
              disabled={nodesEmpty}
            />
            {showFormatAll && (
              <MenuItem
                icon={LayoutDashboard}
                label="Format all nodes"
                onClick={closeAnd(onFormatAll)}
                disabled={nodesEmpty}
              />
            )}
            <MenuItem
              icon={Eraser}
              label="Clear canvas"
              onClick={closeAnd(onClear)}
              disabled={nodesEmpty}
              danger
            />
          </MenuSection>
          <div className="border-t border-gray-100 my-1" />
          <MenuSection title="Import / Export">
            <MenuItem
              icon={jsonCopied ? Check : Copy}
              label={jsonCopied ? "Copied!" : "Copy JSON"}
              onClick={closeAnd(onCopyJSON)}
              active={jsonCopied}
            />
            <MenuItem icon={Upload} label="Import JSON" onClick={closeAnd(onImport)} />
            <MenuItem
              icon={Download}
              label="Export JSON"
              onClick={closeAnd(onExport)}
              disabled={flowNameEmpty}
            />
          </MenuSection>
        </div>
      )}
    </div>
  );
}

export function FlowTestSessionMenu({
  visible,
  testCustomerPhone,
  onCustomerPhoneChange,
  resetSessionLoading,
  resetSessionMessage,
  onReset,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  if (!visible) return null;

  const canReset = testCustomerPhone.trim() && !resetSessionLoading;

  const handleReset = async () => {
    await onReset?.();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-xl border transition-colors ${
          open
            ? "bg-[#E7FCE8] border-[#008069]/40 text-[#006e5a]"
            : "text-[#008069] border-[#008069]/30 bg-[#E7FCE8]/50 hover:bg-[#E7FCE8]"
        }`}
        title="Reset live WhatsApp test session"
      >
        <Smartphone size={16} />
        Test live
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-[#E7FCE8] to-white border-b border-[#008069]/10">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-[#008069]/10">
                <Smartphone size={16} className="text-[#008069]" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900">Test on WhatsApp</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">
                  Reset this customer from all flows and business lines
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Customer test number
              </label>
              <input
                type="tel"
                value={testCustomerPhone}
                onChange={(e) => onCustomerPhoneChange(e.target.value)}
                placeholder="e.g. +91 98765 43210"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#008069]/30 focus:border-[#008069] focus:outline-none bg-white"
              />
              <p className="mt-1.5 text-[10px] text-gray-400">
                WhatsApp number used to message your business — resets across all your flows
              </p>
            </div>

            {testCustomerPhone.trim() && (
              <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Preview
                </p>
                <p className="text-xs text-gray-700">
                  Clear all session progress for{" "}
                  <span className="font-mono font-semibold">{testCustomerPhone.trim()}</span>
                  {" "}on every flow and business phone in your account
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={handleReset}
              disabled={!canReset}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-[#008069] hover:bg-[#006e5a] rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resetSessionLoading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RotateCcw size={16} />
              )}
              {resetSessionLoading ? "Resetting..." : "Reset session"}
            </button>

            {resetSessionMessage && (
              <div
                className={`flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs font-medium ${
                  resetSessionMessage.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-100"
                    : resetSessionMessage.type === "error"
                      ? "bg-red-50 text-red-700 border border-red-100"
                      : "bg-gray-50 text-gray-600 border border-gray-100"
                }`}
              >
                {resetSessionMessage.type === "success" ? (
                  <Check size={14} className="flex-shrink-0 mt-0.5" />
                ) : resetSessionMessage.type === "error" ? (
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                ) : (
                  <Info size={14} className="flex-shrink-0 mt-0.5" />
                )}
                <span>{resetSessionMessage.text}</span>
              </div>
            )}

            <p className="text-[10px] text-gray-400 leading-relaxed">
              Clears saved progress, variables, and cache for this customer on all flows. Send a
              trigger keyword again to start fresh.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
